import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PageTransition from '../components/PageTransition';
import { motion } from 'framer-motion';

const HomePage = () => {
  const [stats, setStats] = useState({ totalTrees: 0, totalUsers: 0, totalCarbon: 0, totalDonated: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Use environment variable for public stats endpoint
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`);
        setStats(res.data);
      } catch (err) {
        console.error("Error fetching public stats", err);
      }
    };
    fetchStats();
  }, []);

  const testimonials = [
      {
        name: "Wangari M.",
        role: "Coffee Farmer, Nyeri",
        quote: "CarbonSasa helped me monetize the Grevillea trees shading my coffee. The carbon credits pay for my fertilizer now.",
        img: "https://randomuser.me/api/portraits/women/12.jpg"
      },
      {
        name: "David K.",
        role: "Real Estate Developer",
        quote: "We use CarbonSasa to track the biodiversity offset for our new housing projects. The dashboard is incredibly detailed.",
        img: "https://randomuser.me/api/portraits/men/32.jpg"
      },
      {
        name: "Sarah J.",
        role: "Student & Eco-Warrior",
        quote: "I planted 5 trees in my parents' compound. Watching their carbon stats grow every month is addictive!",
        img: "https://randomuser.me/api/portraits/women/44.jpg"
      }
  ];

  return (
    <PageTransition>
      {/* Hero Section */}
      <div className="text-center py-20">
        <motion.h1 
          initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="text-5xl md:text-7xl font-extrabold text-brand-green-dark mb-6"
        >
          Plant Trees. <br/><span className="text-brand-gold">Earn Credit.</span>
        </motion.h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
          Join Kenya's fastest growing carbon sequestration platform and be part of a vibrant community committed to combating climate change. Monitor your environmental impact with intuitive dashboards, earn financial rewards for your carbon removal efforts, and contribute to a sustainable future for Kenya and the world. Discover the power of collective action and make a difference today.
        </p>
        <div className="flex justify-center gap-4">
          <Link to="/register" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg font-bold py-3 px-8 rounded-full shadow-lg transition-transform transform hover:scale-105">
            Start Planting
          </Link>
          <Link to="/login" className="bg-white text-brand-green border-2 border-brand-green hover:bg-gray-50 text-lg font-bold py-3 px-8 rounded-full shadow-lg transition-transform transform hover:scale-105">
            Sign In
          </Link>
        </div>
      </div>

      {/* Platform Stats Section */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-10 shadow-xl mb-20 border border-gray-100 dark:border-gray-700">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <h3 className="text-4xl font-bold text-brand-green">{stats.totalTrees}</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wide">Trees Planted</p>
          </div>
          <div>
            <h3 className="text-4xl font-bold text-brand-gold">{stats.totalUsers}</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wide">Active Users</p>
          </div>
          <div>
            <h3 className="text-4xl font-bold text-brand-green-dark">{stats.totalCarbon}k</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wide">Kg CO₂ Removed</p>
          </div>
          <div>
            <h3 className="text-4xl font-bold text-brand-green">{stats.totalDonated}</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wide">KES Donated</p>
          </div>
        </div>
      </div>

      {/* How it Works Section and Testimonials remain the same... */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold text-center text-brand-gray dark:text-white mb-10">How CarbonSasa Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: "🌱", title: "1. Plant", desc: "Plant a tree in your region using our AI-recommended species list." },
            { icon: "📸", title: "2. Monitor", desc: "Upload monthly photos. Our AI verifies growth and health." },
            { icon: "💰", title: "3. Earn", desc: "Receive carbon credits and cash payouts directly to your wallet." }
          ].map((step, idx) => (
            <motion.div 
              whileHover={{ y: -10 }}
              className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md text-center border-t-4 border-brand-green" key={idx}
            >
              <div className="text-5xl mb-4">{step.icon}</div>
              <h3 className="text-xl font-bold text-brand-gray dark:text-white mb-2">{step.title}</h3>
              <p className="text-gray-600 dark:text-gray-300">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-3xl font-bold text-center text-brand-gray dark:text-white mb-10">Community Stories</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-brand-white dark:bg-gray-800 p-6 rounded-xl shadow-sm flex flex-col items-center text-center">
              <img src={t.img} alt={t.name} className="w-20 h-20 rounded-full border-4 border-brand-gold mb-4" />
              <p className="italic text-gray-600 dark:text-gray-300 mb-4">"{t.quote}"</p>
              <h4 className="font-bold text-brand-green-dark dark:text-brand-green-light">{t.name}</h4>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t.role}</span>
            </div>
          ))}
        </div>
      </div>
    </PageTransition>
  );
};

export default HomePage;